import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LegendItem } from "app/shared/data-interaction";
import { Helper } from "app/shared/helper";
import { Logger } from "app/shared/services/logger.service";
import { Chart } from "chart.js";

@Component({
  selector: "app-renewable-prod-chart",
  templateUrl: "./renewable-prod-chart.component.html",
  styleUrls: ["./renewable-prod-chart.component.css"]
})
export class RenewableProdChartComponent implements OnInit {
  @ViewChild("renProdChart") canvas;
  @Input() type;
  @Input() chart_id;
  @Input() options;
  @Input() indicator;
  @Input() sample_data;
  @Input() show_network_mix;
  @Input() chartData;
  @Input() value_type;
  @Input() unit;
  @Input() totals_in_tooltip;

  public renewableProdData: any;
  private chart: Chart;
  private renewableProdDatasets;
  private yAxeLabel_main_ind: string;
  private localOptions;
  private legendItems: LegendItem[] = [];

  constructor(
    private helper: Helper,
    private translateService: TranslateService
  ) {}

  ngOnInit() {}

  ngOnChanges(changes) {
    if (
      !this.helper.isNullOrUndefined(changes.chartData) &&
      !this.helper.isNullOrUndefined(changes.chartData.currentValue)
    ) {
      this.localOptions = JSON.parse(JSON.stringify(this.options));
      this.createRenewableProdChart();
      this.updateLegend();
    }
  }

  getTranslatedLabel(labelToTranslate) {
    return this.translateService.instant("DATA." + labelToTranslate);
  }

  createRenewableProdChart() {
    this.resetChart();
    // For now only data for energy and not CO2
    if (this.indicator == "co2") {
      this.indicator = "energy";
    }
    var unit_from_config = this.helper.getUnitIdFromFilterValue(
      this.value_type,
      this.unit[this.indicator]
    );
    if (this.sample_data) {
      this.renewableProdData = this.getSampleData();
    } else {
      if (this.chartData) {
        this.renewableProdData = { labels: this.chartData["labels"] };
        this.renewableProdData["datasets"] = this.chartData["datasets"];
        var i;
        for (i = 0; i < this.renewableProdData["datasets"].length; i++) {
          this.renewableProdData["datasets"][i][
            "label"
          ] = this.getTranslatedLabel(
            this.renewableProdData["datasets"][i]["label"]
          );
          if (this.value_type != "pourcent" && this.indicator == "energy" && ['energy.abs_kwh', 'energy.inhabitant_kwh'].includes(unit_from_config['translate_unit'])) {
            var j;
            for (
              j = 0;
              j < this.renewableProdData["datasets"][i]["data"].length;
              j++
            ) {
              this.renewableProdData["datasets"][i]["data"][j] *= 1000;
            }
          }
        }
        if (this.value_type != "pourcent" && this.indicator == "energy") {
          for (j = 0; j < this.chartData["totals"].length; j++) {
            this.chartData["totals"][j] *= 1000;
          }
        }
      }
    }

    // DEFINE Y LABEL WITH UNIT
    var y_label_type = "Y_LABEL_NAME";
    this.yAxeLabel_main_ind = this.translateService.instant(
      `DASHBOARD.CHARTS.${this.chart_id}.${y_label_type}`
    );

    this.yAxeLabel_main_ind += this.translateService.instant(
      `UNITS.${unit_from_config["translate_unit"]}`
    );
    // this.localOptions['scales']['yAxes'][0]['scaleLabel']['labelString'] = this.yAxeLabel_main_ind;

    // Define behavior of tooltip for the chart
    this.localOptions.tooltips.callbacks.label = (item, data) => {
      let unit;
      let translate_var = this.value_type;
      unit = this.translateService.instant(
        `UNITS.${unit_from_config["translate_unit"]}`
      );
      return [
        `${data.datasets[item.datasetIndex]
          .label} : ${this.helper.getThousandSeparatorFromNumber(
          item.yLabel
        )} ` + unit
      ];
    };

    const ctx = this.canvas.nativeElement.getContext("2d");
    this.localOptions.scales = {
      yAxes: [
        {
          stacked: true,
          ticks: {
            callback: value => {
              return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
            }
          },
          display: true,
          id: "main_ind",
          scaleLabel: {
            display: true,
            fontColor: "black",
            fontSize: 14,
            labelString: this.yAxeLabel_main_ind
          }
        }
      ]
    };
    this.localOptions.responsive = true;
    this.localOptions.legend.display = false;
    this.localOptions.maintainAspectRatio = false;
    // configure tooltip footer to display total values
    // don't display if not specified in configuration and for % (total will always be 100%)
    if (this.value_type != "pourcent" && this.totals_in_tooltip) {
      this.localOptions.tooltips.callbacks.footer = (item, data) => {
        // hovered data indices
        const dataIndex = item[0].index;
        // raw values
        let total = this.chartData.totals[dataIndex];
        //total *= this.conversionFactor;
        let unit = unit_from_config["translate_unit"];
        // format values
        [total, unit] = this.helper.convertTooBigValue(total, unit);
        total = this.helper.getThousandSeparatorFromNumber(total);
        unit = this.translateService.instant(`UNITS.${unit}`);
        const total_translation = this.translateService.instant(
          `DASHBOARD.CHARTS.${this.chart_id}.TOTAL_TOOLTIP`
        );
        return [total_translation, total + " " + unit];
      };
    } else {
      // disable the footer callback to not display the totals
      this.localOptions.tooltips.callbacks.footer = (item, data) => {
        return [];
      };
    }
    this.chart = new Chart(ctx, {
      type: this.type,
      data: this.renewableProdData,
      options: this.localOptions
    });
  }

  resetChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = void 0;
    }
  }

  updateLegend() {
    this.legendItems = [];
    if (this.chart) {
      // legend element is corresponds to each dataset and its color
      this.chart.data.datasets.forEach(dataset => {
        const legendItem: LegendItem = {
          label: dataset.label,
          color: dataset.backgroundColor,
          borderColor: dataset.backgroundColor,
          borderStyle: "none",
          clicked: false,
          datasetIndices:[]
        };

        this.legendItems.push(legendItem);
      });
    }
  }

  onLegendClick(legendIndex: number) {
    let clickedDataset = this.chart.data.datasets[legendIndex];
    clickedDataset.hidden = !clickedDataset.hidden;
    this.chart.update();
  }

  getSampleData() {
    let renewableProdChartLabels = [2015, 2016, 2017, 2018, 2019, 2020];
    let gas = [10, 15, 20, 25, 30, 25];
    let oil = [10, 15, 20, 25, 30, 25];
    let wood = [10, 15, 20, 25, 30, 25];

    var sampleData = {
      labels: renewableProdChartLabels
    };

    this.renewableProdDatasets = [
      {
        label: "Gaz",
        data: gas,
        stack: "public_bdg",
        backgroundColor: "rgba(51, 51, 0, 0.3)",
        borderColor: "rgba(51, 51, 0)",
        borderWidth: 1,
        lineTension: 0,
        yAxisID: "main_ind"
      },
      {
        label: "Oil",
        data: oil,
        stack: "public_bdg",
        backgroundColor: "rgba(151, 151, 0, 0.3)",
        borderColor: "rgba(51, 51, 0)",
        borderWidth: 1,
        lineTension: 0,
        yAxisID: "main_ind"
      },
      {
        label: "Wood",
        data: wood,
        stack: "public_bdg",
        backgroundColor: "rgba(251, 251, 0, 0.3)",
        borderColor: "rgba(51, 51, 0)",
        borderWidth: 1,
        lineTension: 0,
        yAxisID: "main_ind"
      }
    ];

    sampleData["datasets"] = this.renewableProdDatasets;
    return sampleData;
  }
}
